import { RegExpClass } from '../helpers/RegExpClass';
import { toTwoDecimalPlaces } from '../helpers/arithmetic';
import { IPhoneNumber, ICostTableValue, ISalaryRange } from '../types';

export const PhoneNumberValidator = (val: IPhoneNumber): string | null => {
    const deFormattedVal = val?.number?.replace(/\(|\)|#| /g, '') ?? '';

    if (deFormattedVal && !Boolean(deFormattedVal?.toString().match(RegExpClass.PHONE_NUMBER_STRIPPED))) {
        return 'phone.invalidMessages.format';
    } else if (val?.ext && !deFormattedVal) {
        return 'phone.invalidMessages.extWithoutNum';
    }
    return null;
};

export const WageRangerValidation = (
    workerType: string,
    val: ISalaryRange[],
    values: Record<string, any>
): Record<string, string | null> | null => {
    const isEmployee = workerType === 'employee';
    const totalAllocatedEmployees = val.reduce((acc, { total }) => (total ? acc + total : acc), 0);
    const totalError =
        totalAllocatedEmployees !== values[isEmployee ? 'employeeCount' : 'contractorsWithHourlyRateCount']
            ? 'wageRanger.invalidMessages.sumTotal'
            : null;

    const rangeErrors: Record<string, string | null> = val.reduce((acc, { total = 0, ft = 0, pt = 0 }, index) => {
        return total !== ft + pt
            ? {
                  ...acc,
                  [`range_${index}`]: 'wageRanger.invalidMessages.wageTierSum'
              }
            : acc;
    }, {});

    if (!totalError && !Object.keys(rangeErrors).length) return null;

    return {
        total: totalError,
        ...rangeErrors
    };
};

export const NoNegativeInt = (val: number, values: Record<string, any>): string | null => {
    return val && val < 0 ? 'common.invalidMessages.numberTooLow' : null;
};
export const CannotExceedTotalEmployeeCount = (val: number, values: Record<string, any>): string | null => {
    if (val && val < 0) return 'common.invalidMessages.numberTooLow';
    return val > values.employeeCount ? 'common.invalidMessages.exceedsEmployeeCount' : null;
};
export const CannotExceedTotalContractorCount = (val: number, values: Record<string, any>): string | null => {
    if (val && val < 0) return 'common.invalidMessages.numberTooLow';
    return val > values.contractorsWithHourlyRateCount ? 'common.invalidMessages.exceedsEmployeeCount' : null;
};

export const MonthsOfYearCountValidation = (val: number, values: Record<string, any>): string | null => {
    if (typeof val !== 'number') return 'common.invalidMessages.invalidNumber';
    if (val > 12) {
        return 'common.invalidMessages.monthTooHigh';
    } else if (val < 0) {
        return 'common.invalidMessages.numberTooLow';
    }
    return null;
};

export const HoursPerWeekValidation = (val: number, values: Record<string, any>): string | null => {
    if (typeof val !== 'number') return 'common.invalidMessages.invalidNumber';
    if (val > 168) {
        return 'common.invalidMessages.hoursInWeekTooHigh';
    } else if (val < 0) {
        return 'common.invalidMessages.numberTooLow';
    }
    return null;
};

export const HouseholdCount = (val: number, values: Record<string, any>): string | null => {
    if (typeof val !== 'number') return 'common.invalidMessages.invalidNumber';
    return val > 25 ? 'sharedIncomeOrExpensesCount.invalidMessages.numberTooHigh' : null;
};

export const FinancialsAtLeastOneValue = (values: Record<string, any>): string | null => {
    return !values || !Object.values(values).some((val) => typeof val !== 'undefined' && val !== null)
        ? 'common.invalidMessages.mustProvideOneFinancial'
        : null;
};

export const FinancialsRequiredItems = (
    values: Record<string, any>,
    requiredValueNames?: string[]
): Record<string, string | null> | null => {
    const errors = (requiredValueNames ?? []).reduce(
        (acc, curr) =>
            typeof values?.[curr] !== 'undefined' && values?.[curr] !== null
                ? acc
                : {
                      ...acc,
                      [curr]: 'Required'
                  },
        {}
    );

    return Object.keys(errors).length ? errors : null;
};
